import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Banner, EmptyState, Layout, LegacyCard, LegacyStack, Page, Text } from '@shopify/polaris';
import { getDeposits, getMultiDepositStock } from '../../../services/depositsService';
import { getSearchParams, handleSearchParams } from '../../../helpers/handleSearchParams';
import ProductFilters from '../../UI/Products/ProductFilters';
import ProductsWrapper from '../../UI/Products/ProductsWrapper';
import ProductStockTable from '../../UI/Tables/ProductStockTable';
import DropdownFilter from '../../UI/Common/DropdownFilter';
import DropdownButton from '../../UI/Common/DropdownButton';
import { PDFModal } from '../../UI/Modals/PDFModal';

const PAGE_LIMIT = 10;

/**
 * Vista de stock CONJUNTO de varios depósitos.
 *
 * Todo el estado —la selección de depósitos y los filtros de catálogo— vive en
 * la query string (`/deposits/multi?deposits=1:2:3&s=…&genders=…`), de modo que
 * la vista sea enlazable y recargable. `ProductFilters` ya sincroniza sus
 * propios filtros con la URL; aquí sólo gestionamos `deposits`.
 *
 * La tabla es la misma que la de la ficha de producto: al no pasarle el prop
 * `deposit`, `ProductStockTable` pinta una fila por depósito más la fila TOTAL.
 */
const ViewMultiDeposits = () => {
  const history = useHistory();

  const [depositsValue, setDepositsValue] = useState(() => getSearchParams('deposits') || '');
  const [depositOptions, setDepositOptions] = useState([]);
  const [stock, setStock] = useState(null);
  const [filters, setFilters] = useState(null);
  const [numbers, setNumbers] = useState({});
  const [error, setError] = useState(null);
  const [activePDFModal, setActivePDFModal] = useState(false);

  const selectedIds = depositsValue.split(':').filter(Boolean);

  useEffect(() => {
    getDeposits({ limit: 1000, excludeGroups: true })
      .then((r) => setDepositOptions((r.data.data || []).map((d) => ({ label: d.name, value: String(d.id) }))))
      .catch(() => setDepositOptions([]));
  }, []);

  const loadStock = (page = 1, currentFilters, rebuild = false) => {
    if (!depositsValue) return;

    return getMultiDepositStock({ deposits: depositsValue, page, limit: PAGE_LIMIT, ...currentFilters })
      .then((r) => {
        setError(null);
        setStock({ ...r.data, new: rebuild });
        setNumbers({
          stockQty: r.data.metadata.stockQty,
          distinctProducts: r.data.metadata.distinctProducts,
          distinctVariants: r.data.metadata.distinctVariants,
        });
      })
      .catch((e) => {
        setStock(null);
        setError(e.response?.data?.message || 'No se ha podido cargar el stock conjunto');
      });
  };

  // `filters` arranca a null hasta que ProductFilters hidrata desde la URL; así
  // evitamos una primera petición sin filtros que luego se repetiría.
  useEffect(() => {
    if (filters) loadStock(1, filters, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, depositsValue]);

  const handleDepositsChange = ({ value }) => {
    handleSearchParams('deposits', value);
    setDepositsValue(value);
  };

  const depositsLabel = selectedIds.length
    ? `${selectedIds.length} depósito${selectedIds.length > 1 ? 's' : ''}`
    : 'Selecciona depósitos';

  // El PDF recibe la misma query que la vista (depósitos + filtros aplicados).
  const pdfQuery = { deposits: depositsValue, ...(filters || {}) };

  return (
    <Page
      backAction={{ content: 'Ver depósitos', onAction: () => history.push('/deposits') }}
      title="Multidepósito"
      subtitle={depositsLabel}
      primaryAction={
        <DropdownButton
          title={'Exportar'}
          items={[
            {
              content: 'Exportar a PDF',
              disabled: !depositsValue || !stock?.data?.length,
              onAction: () => setActivePDFModal(true),
            },
            { content: 'Exportar a CSV', disabled: true },
          ]}
          buttonProps={{ disabled: !depositsValue || !stock?.data?.length }}
        />
      }
      divider
    >
      <Layout>
        <Layout.Section>
          <LegacyCard>
            {Object.keys(numbers).length > 0 && (
              <LegacyCard.Section>
                <LegacyStack distribution="fill">
                  <span>
                    <Text variant="headingXs" weight="semibold">
                      Uds.
                    </Text>
                    <Text variant="heading3xl">{numbers.stockQty ?? 0}</Text>
                  </span>
                  <span>
                    <Text variant="headingXs" weight="semibold">
                      Serie-Color
                    </Text>
                    <Text variant="heading3xl">{numbers.distinctProducts ?? 0}</Text>
                  </span>
                  <span>
                    <Text variant="headingXs" weight="semibold">
                      SKU
                    </Text>
                    <Text variant="heading3xl">{numbers.distinctVariants ?? 0}</Text>
                  </span>
                </LegacyStack>
              </LegacyCard.Section>
            )}
            <LegacyCard.Section title="Depósitos">
              <DropdownFilter
                slug="deposits"
                title={depositsLabel}
                options={depositOptions}
                defaultValue={depositsValue}
                onFilterChange={handleDepositsChange}
              />
            </LegacyCard.Section>
            <LegacyCard.Section title="Filtros">
              <ProductFilters onFilterSelected={(f) => setFilters(f)} />
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>

        <Layout.Section>
          {error && (
            <Banner status="critical" title="Error al cargar el stock">
              <p>{error}</p>
            </Banner>
          )}

          {!depositsValue && (
            <LegacyCard>
              <LegacyCard.Section>
                <EmptyState
                  heading="Selecciona al menos un depósito"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Elige los depósitos que quieras comparar para ver su stock conjunto.</p>
                </EmptyState>
              </LegacyCard.Section>
            </LegacyCard>
          )}

          {depositsValue && stock && !stock.data.length && (
            <LegacyCard>
              <LegacyCard.Section>
                <EmptyState
                  heading="Los depósitos seleccionados no contienen mercancía"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                />
              </LegacyCard.Section>
            </LegacyCard>
          )}

          {depositsValue && stock && (
            <ProductsWrapper
              data={stock}
              TableComponent={ProductStockTable}
              loadPage={() => loadStock(stock.metadata.page + 1, filters)}
            />
          )}
        </Layout.Section>
      </Layout>

      <PDFModal
        active={activePDFModal}
        onClose={() => setActivePDFModal(false)}
        domain={'deposits'}
        model={'stock'}
        query={pdfQuery}
      />
    </Page>
  );
};

export default ViewMultiDeposits;
